import PipelinesAPI from '../../api/pipelines';
import {
  capturePipelineAccess,
  kanbanEnabled,
} from 'dashboard/permissionSets/kanbanReads';

const state = {
  readVersion: 0,
  records: [],
  defaultPipelineId: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

const getters = {
  getPipelines: $state => $state.records,
  getDefaultPipelineId: $state => $state.defaultPipelineId,
  getPipelineById: $state => id => $state.records.find(p => p.id === id),
  getPipelineByKey: $state => key => $state.records.find(p => p.key === key),
  getDefaultPipeline: $state =>
    $state.records.find(p => p.id === $state.defaultPipelineId),
  getUIFlags: $state => $state.uiFlags,
  hasPipelines: $state => $state.records.length > 0,
};

const actions = {
  async get(context) {
    const { commit, rootGetters } = context;
    if (!kanbanEnabled(rootGetters)) return null;
    const isCurrent = capturePipelineAccess(context);
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await PipelinesAPI.getPipelines();
      if (!isCurrent()) return null;
      commit('SET_PIPELINES', response.data);
      return response.data;
    } catch (error) {
      if (!isCurrent()) return null;
      throw error;
    } finally {
      if (isCurrent()) commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async create(context, pipelineData) {
    const { commit } = context;
    const isCurrent = capturePipelineAccess(context);
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const response = await PipelinesAPI.createPipeline(pipelineData);
      if (!isCurrent()) return null;
      commit('ADD_PIPELINE', response.data.pipeline);
      return response.data.pipeline;
    } finally {
      if (isCurrent()) commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async update(context, { id, ...pipelineData }) {
    const { commit } = context;
    const isCurrent = capturePipelineAccess(context);
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await PipelinesAPI.updatePipeline(id, pipelineData);
      if (!isCurrent()) return null;
      commit('UPDATE_PIPELINE', response.data.pipeline);
      return response.data.pipeline;
    } finally {
      if (isCurrent()) commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async delete(context, id) {
    const { commit } = context;
    const isCurrent = capturePipelineAccess(context);
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await PipelinesAPI.deletePipeline(id);
      if (!isCurrent()) return null;
      commit('DELETE_PIPELINE', id);
      return true;
    } finally {
      if (isCurrent()) commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  async setDefault(context, id) {
    const { commit } = context;
    const isCurrent = capturePipelineAccess(context);
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await PipelinesAPI.setDefaultPipeline(id);
      if (!isCurrent()) return null;
      commit('SET_DEFAULT_PIPELINE', id);
      commit('SET_PIPELINES', response.data);
      return response.data;
    } finally {
      if (isCurrent()) commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async reorderPipelines(context, pipelineIds) {
    const { commit } = context;
    const isCurrent = capturePipelineAccess(context);
    const response = await PipelinesAPI.reorderPipelines(pipelineIds);
    if (!isCurrent()) return null;
    commit('REORDER_PIPELINES', response.data.pipelines);
    return response.data.pipelines;
  },

  async createStage(context, { pipelineId, ...stageData }) {
    const { commit } = context;
    const isCurrent = capturePipelineAccess(context);
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await PipelinesAPI.createStage(pipelineId, stageData);
      if (!isCurrent()) return null;
      commit('ADD_STAGE', { pipelineId, stage: response.data.stage });
      return response.data.stage;
    } finally {
      if (isCurrent()) commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async updateStage(context, { pipelineId, stageId, ...stageData }) {
    const { commit } = context;
    const isCurrent = capturePipelineAccess(context);
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await PipelinesAPI.updateStage(
        pipelineId,
        stageId,
        stageData
      );
      if (!isCurrent()) return null;
      commit('UPDATE_STAGE', {
        pipelineId,
        stageId,
        stage: response.data.stage,
      });
      return response.data.stage;
    } finally {
      if (isCurrent()) commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async deleteStage(context, { pipelineId, stageId }) {
    const { commit } = context;
    const isCurrent = capturePipelineAccess(context);
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await PipelinesAPI.deleteStage(pipelineId, stageId);
      if (!isCurrent()) return null;
      commit('DELETE_STAGE', { pipelineId, stageId });
      return true;
    } finally {
      if (isCurrent()) commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  async reorderStages(context, { pipelineId, stageIds }) {
    const { commit } = context;
    const isCurrent = capturePipelineAccess(context);
    const response = await PipelinesAPI.reorderStages(pipelineId, stageIds);
    if (!isCurrent()) return null;
    commit('REORDER_STAGES', { pipelineId, stages: response.data.stages });
    return response.data.stages;
  },
};

const mutations = {
  CLEAR_PIPELINES($state) {
    $state.readVersion += 1;
    $state.records = [];
    $state.defaultPipelineId = null;
    $state.uiFlags = {
      isFetching: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
    };
  },

  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  SET_PIPELINES($state, data) {
    $state.records = data.pipelines || [];
    $state.defaultPipelineId = data.default_pipeline_id;
  },

  ADD_PIPELINE($state, pipeline) {
    $state.records.push(pipeline);
  },

  UPDATE_PIPELINE($state, pipeline) {
    const index = $state.records.findIndex(p => p.id === pipeline.id);
    if (index !== -1) {
      $state.records.splice(index, 1, pipeline);
    }
  },

  DELETE_PIPELINE($state, id) {
    $state.records = $state.records.filter(p => p.id !== id);
    if ($state.defaultPipelineId === id && $state.records.length > 0) {
      $state.defaultPipelineId = $state.records[0].id;
    }
  },

  SET_DEFAULT_PIPELINE($state, id) {
    $state.defaultPipelineId = id;
  },

  REORDER_PIPELINES($state, pipelines) {
    $state.records = pipelines;
  },

  ADD_STAGE($state, { pipelineId, stage }) {
    const pipeline = $state.records.find(p => p.id === pipelineId);
    if (pipeline) {
      pipeline.stages = pipeline.stages || [];
      pipeline.stages.push(stage);
    }
  },

  UPDATE_STAGE($state, { pipelineId, stageId, stage }) {
    const pipeline = $state.records.find(p => p.id === pipelineId);
    if (pipeline && pipeline.stages) {
      const index = pipeline.stages.findIndex(s => s.id === stageId);
      if (index !== -1) {
        pipeline.stages.splice(index, 1, stage);
      }
    }
  },

  DELETE_STAGE($state, { pipelineId, stageId }) {
    const pipeline = $state.records.find(p => p.id === pipelineId);
    if (pipeline && pipeline.stages) {
      pipeline.stages = pipeline.stages.filter(s => s.id !== stageId);
    }
  },

  REORDER_STAGES($state, { pipelineId, stages }) {
    const pipeline = $state.records.find(p => p.id === pipelineId);
    if (pipeline) {
      pipeline.stages = stages;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
